import React from 'react';
import { Drawer } from 'vaul';
import { useNavigate } from 'react-router-dom';
import { X, ExternalLink, MapPin, Calendar, Linkedin, ArrowRight, AlertCircle } from 'lucide-react';
import Logo from './Logo';
import { linkedinCompanyUrl } from '../lib/linkedin';

const Chip = ({ children, className = '' }) => (
  <span className={`inline-flex items-center text-[11px] font-medium px-2 py-1 rounded-md bg-slate-100 text-slate-700 border border-slate-200 ${className}`}>
    {children}
  </span>
);

const SectionLabel = ({ children }) => (
  <div className="text-[11px] font-bold tracking-[0.2em] text-slate-400 uppercase mt-7 mb-3">{children}</div>
);

const MobileDetailSheet = ({ startup, onClose, onReportIssue }) => {
  const navigate = useNavigate();
  if (!startup) return null;

  const founders = startup.founders || [];
  const investors = startup.investors || [];
  const website = (startup.website || '').replace(/^https?:\/\//, '');
  const li = linkedinCompanyUrl(startup);

  return (
    <Drawer.Root open={!!startup} onOpenChange={(o) => { if (!o) onClose(); }}>
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 z-[1200] bg-black/25" />
        <Drawer.Content
          data-testid="mobile-detail-sheet"
          className="fixed inset-x-0 bottom-0 z-[1210] flex h-[88vh] flex-col rounded-t-[24px] bg-white outline-none"
        >
          <div className="mx-auto mt-3 h-1.5 w-12 rounded-full bg-slate-200 shrink-0" />

          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 shrink-0">
            <Drawer.Title className="text-[11px] font-bold tracking-[0.2em] text-slate-400 uppercase">
              Details
            </Drawer.Title>
            <Drawer.Description className="sr-only">Startup details</Drawer.Description>
            <button
              onClick={onClose}
              data-testid="mobile-detail-close"
              aria-label="Close details"
              className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 hover:bg-slate-200"
            >
              <X size={16} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-4 pb-32">
            <div className="flex items-start gap-3 pt-4">
              <Logo startup={startup} size={52} />
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-bold text-slate-900 leading-tight">{startup.name}</h2>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {startup.sector && <Chip>{startup.sector}</Chip>}
                  {startup.badge && <Chip>{startup.badge}</Chip>}
                  {startup.yc && <Chip className="bg-orange-500 text-white border-orange-500">Y Combinator</Chip>}
                </div>
              </div>
            </div>

            {startup.description && (
              <p className="text-sm text-slate-600 leading-relaxed mt-4">{startup.description}</p>
            )}

            <SectionLabel>Founders</SectionLabel>
            {founders.length ? (
              <div className="flex flex-col gap-2.5">
                {founders.map((f, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-800">{f.name}</span>
                    {f.linkedin && (
                      <a href={f.linkedin} target="_blank" rel="noreferrer" aria-label={`${f.name} on LinkedIn`} className="text-[#0A66C2]">
                        <Linkedin size={18} />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-400">No founders listed yet.</p>
            )}

            <SectionLabel>Investors</SectionLabel>
            {investors.length ? (
              <div className="flex flex-wrap gap-2">
                {investors.map((inv, i) => <Chip key={i}>{inv}</Chip>)}
              </div>
            ) : (
              <p className="text-sm text-slate-400">—</p>
            )}

            <SectionLabel>Address</SectionLabel>
            <div className="border border-slate-200 rounded-xl p-4 bg-slate-50/50 flex items-start gap-2 text-sm text-slate-700">
              <MapPin size={15} className="text-rose-500 mt-0.5 shrink-0" fill="currentColor" />
              <span>{startup.address}</span>
            </div>

            <SectionLabel>Upcoming events</SectionLabel>
            <div className="border border-slate-200 rounded-xl p-4 bg-slate-50/50 text-sm text-slate-500 flex items-center gap-2">
              <Calendar size={15} className="text-slate-400 shrink-0" />
              {startup.events ? 'Event this week — check the Events panel.' : 'No upcoming event.'}
            </div>
          </div>

          <div className="absolute bottom-0 inset-x-0 p-4 bg-white/95 backdrop-blur-xl border-t border-slate-100 flex flex-col gap-3">
            {website && (
              <a
                href={`https://${website}`}
                target="_blank"
                rel="noreferrer"
                data-testid="mobile-detail-website"
                className="inline-flex items-center justify-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-full py-3 text-sm font-semibold shadow-md"
              >
                Visit website <ExternalLink size={15} />
              </a>
            )}
            <div className="flex items-center justify-between">
              <button
                onClick={() => navigate(`/company/${startup.id}`)}
                data-testid="mobile-detail-full-profile"
                className="inline-flex items-center gap-1 text-sm font-semibold text-slate-700 hover:text-slate-900"
              >
                Full company profile <ArrowRight size={15} />
              </button>
              <button
                onClick={() => { onClose(); if (onReportIssue) onReportIssue(); }}
                data-testid="mobile-detail-report"
                className="inline-flex items-center gap-1 text-[13px] text-slate-400 hover:text-rose-600"
              >
                <AlertCircle size={13} /> Spot something wrong?
              </button>
            </div>
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
};

export default MobileDetailSheet;
